/**
 * The Environment, Health, Safety teaser band on the Ryerson Commitment landing page
 * (`src/pages/why-ryerson/ryerson-commitment/index.tsx`, its only user): an image, a short
 * paragraph and a "More about" button that opens the full page,
 * `/why-ryerson/ryerson-commitment/environment-health-safety/`.
 *
 * It has no layout of its own: it renders the Product Quality band's layouts,
 * `../ProductQuality/ProductQualityMobile.tsx` below `lg` and `ProductQualityDesktop.tsx` from
 * `lg` up, with the copy in `./Content.tsx`. So `EnvironmentHealthAndSafetyProps` must stay
 * assignable to `../ProductQuality/ProductQuality.tsx` → `ProductQualityProps`.
 *
 * Not to be confused with the sibling `../EnvironmentHealthSafety/` folder, which holds the
 * sections of the full page (and whose unused `EnvironmentHealthSafetyHeader.tsx` also has a
 * default export named `EnvironmentHealthAndSafety`).
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { Media } from "@ryerson/frontend.next";
import { EnvironmentHealthAndSafetyContent } from "./Content";
import ProductQualityDesktop from "../ProductQuality/ProductQualityDesktop";
import ProductQualityMobile from "../ProductQuality/ProductQualityMobile";

/** Theme type for the band's `ContentSection`. */
export type backgroundType = "primary" | "secondary";

/** Copy and link for the teaser band; see `./Content.tsx`. */
export interface EnvironmentHealthAndSafetyProps {
	/**
	 * Band theme. `./Content.tsx` doesn't set it, so the Product Quality layouts' defaults apply:
	 * `"secondary"` on desktop, `"primary"` on mobile.
	 */
	background?: backgroundType;
	/** Site-relative path of a file in `application/public/`. */
	imageUrl: string;
	title: string;
	/** The teaser paragraph. */
	content: string;
	/** Button text; a `\n` is a line break (the label is styled `white-space: pre-line`). */
	buttonLabel: string;
	/** Site path the button opens. */
	moreAboutLink: string;
}

/** Renders the teaser in the current language: mobile layout below `lg`, desktop from `lg` up. */
const EnvironmentHealthAndSafety: React.FC = () => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<>
			<Media lessThan="lg">
				<ProductQualityMobile {...EnvironmentHealthAndSafetyContent[language]} />
			</Media>
			<Media greaterThanOrEqual="lg">
				<ProductQualityDesktop {...EnvironmentHealthAndSafetyContent[language]} />
			</Media>
		</>
	);
};

export default EnvironmentHealthAndSafety;
